import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import initApi from '@/api/initApiV2.ts';

import {
  AgentTestApi,
  type AgentTest,
  type AgentTestApiCreateAgentTestRequest,
  type AgentTestApiDeleteAgentTestRequest,
  type AgentTestApiGetAgentTestDefinitionChangesRequest,
  type AgentTestApiGetAgentTestRequest,
  type AgentTestApiGetAgentTestRunsRequest,
  type AgentTestApiGetAgentTestsRequest,
  type AgentTestApiRunAgentTestRequest,
  type AgentTestApiUpdateAgentTestRequest,
  type AgentTestPaginated,
  type AgentTestRunPaginated,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';

export const useAgentTestStore = defineStore('agent-test-store', () => {
  const currentAgentTest = ref<AgentTest | null>(null);
  const agentTestsData = ref<AgentTestPaginated | null>(null);
  const agentTestRunsData = ref<AgentTestRunPaginated | null>(null);

  const resetAgentTest = () => {
    currentAgentTest.value = null;
    agentTestRunsData.value = null;
  };

  const getAgentTests = async (
    params: AgentTestApiGetAgentTestsRequest,
    updateStore: boolean = true,
  ) => {
    try {
      const agentTestApi = await initApi(AgentTestApi);
      const response = await agentTestApi?.getAgentTests(params);

      if (!response) return;

      if (updateStore) {
        agentTestsData.value = response.data;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getAgentTest = async (params: AgentTestApiGetAgentTestRequest) => {
    try {
      const agentTestApi = await initApi(AgentTestApi);
      const response = await agentTestApi?.getAgentTest(params);

      if (!response) return;

      currentAgentTest.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createAgentTest = async (params: AgentTestApiCreateAgentTestRequest) => {
    try {
      const agentTestApi = await initApi(AgentTestApi);
      const response = await agentTestApi?.createAgentTest(params);

      if (!response) return;

      currentAgentTest.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateAgentTest = async (params: AgentTestApiUpdateAgentTestRequest) => {
    try {
      const agentTestApi = await initApi(AgentTestApi);
      const response = await agentTestApi?.updateAgentTest(params);

      if (!response) return;

      currentAgentTest.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const deleteAgentTest = async (params: AgentTestApiDeleteAgentTestRequest) => {
    try {
      const agentTestApi = await initApi(AgentTestApi);
      await agentTestApi?.deleteAgentTest(params);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getAgentTestRuns = async (
    params: AgentTestApiGetAgentTestRunsRequest,
    updateStore: boolean = true,
  ) => {
    try {
      const agentTestApi = await initApi(AgentTestApi);
      const response = await agentTestApi?.getAgentTestRuns(params);

      if (!response) return;

      if (updateStore) {
        agentTestRunsData.value = response.data;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const runAgentTest = async (params: AgentTestApiRunAgentTestRequest) => {
    try {
      const agentTestApi = await initApi(AgentTestApi);
      const response = await agentTestApi?.runAgentTest(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getAgentTestDefinitionChanges = async (
    params: AgentTestApiGetAgentTestDefinitionChangesRequest,
  ) => {
    try {
      const agentTestApi = await initApi(AgentTestApi);
      const response = await agentTestApi?.getAgentTestDefinitionChanges(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  return {
    currentAgentTest,
    agentTestsData,
    agentTestRunsData,
    resetAgentTest,
    getAgentTests,
    getAgentTest,
    createAgentTest,
    updateAgentTest,
    deleteAgentTest,
    getAgentTestRuns,
    runAgentTest,
    getAgentTestDefinitionChanges,
  };
});
